import { cloneElement, useRef, useState, type ReactElement, type ElementType, type HTMLAttributes, type Ref } from "react";
import { mergeProps, useButton } from "react-aria";
import {
  Dialog,
  DialogTrigger,
  Popover,
  type PopoverProps,
} from "react-aria-components";

import { cn } from "@/shared/lib/cn";
import {
  MerryDatePicker,
  type MerryDatePickerProps,
  type MerryDateRange,
} from "@/shared/ui/MerryDatePicker";

type DropdownProps = {
  /** 原生元素或基于 React Aria 的按钮（如 MerryButton）。原生非按钮元素自动补齐键盘与按钮语义。 */
  children: ReactElement<HTMLAttributes<HTMLElement> & { ref?: Ref<HTMLElement> }>;
  isOpen?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  placement?: PopoverProps["placement"];
  offset?: number;
  popoverClassName?: string;
  /** 完成单日或完整区间选择后关闭，默认开启。 */
  closeOnSelect?: boolean;
};

export type MerryDatePickerDropdownProps = MerryDatePickerProps & DropdownProps;

function NativeDatePickerTrigger({ children, isDisabled }: Pick<DropdownProps, "children"> & { isDisabled?: boolean }) {
  const ref = useRef<HTMLElement>(null);
  const { buttonProps } = useButton({ elementType: children.type as ElementType, isDisabled }, ref);
  // 仅将 ref 传给克隆元素；DOM ref 的合并发生在挂载回调中。
  // eslint-disable-next-line react-hooks/refs
  return cloneElement(children, {
    ...mergeProps(children.props, buttonProps),
    ref: (element: HTMLElement | null) => {
      ref.current = element;
      const childRef = children.props.ref;
      if (typeof childRef === "function") return childRef(element);
      // 在 React 挂载回调中转发调用方的对象 ref。
      // eslint-disable-next-line react-hooks/immutability
      if (childRef) childRef.current = element;
    },
  });
}

/**
 * 为日历面板添加锚定弹层；年月标题可切换到对应选择视图。
 * @example
 * <MerryDatePickerDropdown aria-label="选择日期" value={date} onChange={setDate}>
 *   <MerryButton>选择日期</MerryButton>
 * </MerryDatePickerDropdown>
 * 区间使用 mode="range" 和 { start, end } 值；defaultView="year" 可从年份开始。
 */
export function MerryDatePickerDropdown(props: MerryDatePickerDropdownProps) {
  const {
    children,
    isOpen,
    defaultOpen = false,
    onOpenChange,
    placement = "bottom start",
    offset = 8,
    popoverClassName,
    closeOnSelect = true,
    ...calendarProps
  } = props;
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const changeOpen = (open: boolean) => {
    setInternalOpen(open);
    onOpenChange?.(open);
  };
  const selectionComplete = () => {
    if (closeOnSelect) changeOpen(false);
  };

  return (
    <DialogTrigger
      isOpen={!props.isDisabled && (isOpen ?? internalOpen)}
      onOpenChange={(open) => {
        if (!props.isDisabled || !open) changeOpen(open);
      }}
    >
      {typeof children.type === "string" ? (
        <NativeDatePickerTrigger isDisabled={props.isDisabled}>{children}</NativeDatePickerTrigger>
      ) : children}
      <Popover
        className={cn("merry-date-picker-dropdown", popoverClassName)}
        containerPadding={12}
        offset={offset}
        placement={placement}
        shouldFlip
      >
        <Dialog aria-label={props["aria-label"]} className="merry-date-picker-dropdown__dialog">
          {calendarProps.mode === "range" ? (
            <MerryDatePicker
              {...calendarProps}
              autoFocus
              onChange={(value: MerryDateRange) => {
                calendarProps.onChange?.(value);
                selectionComplete();
              }}
            />
          ) : (
            <MerryDatePicker
              {...calendarProps}
              autoFocus
              onChange={(value) => {
                calendarProps.onChange?.(value);
                selectionComplete();
              }}
            />
          )}
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}
